import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Send, Loader2 } from "lucide-react";

/**
 * "Refine this chart" pill input — Feature #1. Replays the viz pipeline
 * over the cached rows with a new intent; submission bubbles up to
 * ChatPage, which pushes a new turn.
 *
 * <p>Visually paired with the AskAboutDataInput below it — same shape,
 * different accent on the send button so the user reads them as
 * siblings, not paradigms.
 */
const PROMPT_HINTS = [
  "show as a line chart",
  "sort descending",
  "top 5 only",
  "use a green palette",
  "group by quarter",
];

interface Props {
  columnNames: string[];
  rows: Array<Array<unknown>>;
  busy: boolean;
  onSubmit: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
  /** Optional sibling slot — used by RerunWithFiltersButton. */
  extraAction?: ReactNode;
}

export function RefineChartInput({ columnNames, rows, busy, onSubmit, extraAction }: Props) {
  const [draft, setDraft] = useState("");
  const placeholder = useMemo(
    () => `Refine: e.g. ${PROMPT_HINTS[Math.floor(Math.random() * PROMPT_HINTS.length)]}…`,
    [],
  );
  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    onSubmit(trimmed, columnNames, rows);
    setDraft("");
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };
  return (
    <div
      role="group"
      aria-label="Refine chart"
      className="mt-3 flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100"
    >
      <input
        type="text"
        className="flex-1 min-w-0 border-0 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 placeholder:italic focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-400"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
        aria-label="Refine chart prompt"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || draft.trim().length === 0}
        aria-label={busy ? "Refining…" : "Send refinement"}
        title={busy ? "Refining…" : "Send refinement"}
        className="flex-none inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white transition hover:-translate-y-px hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
      </button>
      {extraAction}
    </div>
  );
}
